import React, { useState, useEffect } from 'react';
import { X, MapPin, Users, Coffee, CalendarCheck, Phone, CheckCircle, Award, Clock } from 'lucide-react';
import { useVenueStore } from '../store/useVenueStore';
import { useTranslation } from 'react-i18next';

interface Question {
  id: string;
  text: string;
  options?: string[];
  autoAdvance?: boolean;
}

// List of allowed venues - using exact IDs from venue data
const ALLOWED_VENUES = [
  'jacyz-hotel', 
  'svenska-massan',
  'hjortviken',
  'pigalle',
  'bellora',
  'johanneberg'
];

// Jacy'z phone number
const JACYZ_PHONE = "031-720 80 80";

export const AIAssistantDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState<string | null>(null);
  const [recommendedVenue, setRecommendedVenue] = useState<any>(null);
  const [jacyzVenue, setJacyzVenue] = useState<any>(null);
  const { venues } = useVenueStore();
  const [inputValue, setInputValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);

  // Define translated questions within the component
  const translatedQuestions: Question[] = [
    { 
      id: 'email', 
      text: t('aiAssistant.survey.intro'), 
      autoAdvance: false 
    },
    { id: 'importance', text: t('aiAssistant.survey.features'), autoAdvance: false },
    { id: 'people', text: t('aiAssistant.survey.people'), autoAdvance: false },
    { id: 'teambuilding', text: t('aiAssistant.survey.purpose'), autoAdvance: false }
  ];

  // Get the current question based on current step
  const currentQuestion = translatedQuestions[currentStep] || translatedQuestions[0];

  // Function to get complete venue information
  const getFullVenueData = (venueId: string) => {
    // First try to find in filtered venues (which might have more details)
    const fromFiltered = venues.find(v => v.id.includes(venueId));
    if (fromFiltered) return fromFiltered;
    
    // Otherwise check in allVenues if available
    if (venues) {
      const fromAll = venues.find(v => v.id.includes(venueId));
      if (fromAll) return fromAll;
    }
    
    // Fallback to venues again
    return venues.find(v => v.id.includes(venueId));
  };

  // Get Jacy'z venue info on mount
  useEffect(() => {
    const jacyzInfo = getFullVenueData('jacyz-hotel');
    if (jacyzInfo) {
      setJacyzVenue(jacyzInfo);
    }
  }, [venues]);

  useEffect(() => {
    // Add a console.log to debug
    console.log('AIAssistantDialog opened');
    
    // Optional: add history state to allow back button to close the modal
    window.history.pushState({ ai: 'open' }, '', window.location.pathname);
    
    return () => {
      console.log('AIAssistantDialog closed');
    };
  }, []);

  const handleAnswer = async (answer: string) => {
    const currentQuestion = translatedQuestions[currentStep];
    
    // If already submitting, ignore additional clicks
    if (isSubmitting) return;
    
    // Validate minimum character requirements for specific questions
    if (currentQuestion.id === 'importance' && answer.length < 100) {
      alert('Vänligen beskriv vad som är viktigt för er med minst 100 tecken');
      return;
    }
    
    if (currentQuestion.id === 'teambuilding' && answer.length < 100) {
      alert('Vänligen beskriv önskade teambuilding-aktiviteter med minst 100 tecken');
      return;
    }
    
    if (currentQuestion.id === 'email') {
      try {
        setIsSubmitting(true);
        
        // Log the request for debugging
        console.log('Sending email to newsletter:', answer);
        
        // Anropa newsletter-signup funktionen
        const response = await fetch('/.netlify/functions/newsletter-signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: answer }),
        });
        
        const result = await response.json();
        console.log('Newsletter signup result:', result);
        
        if (!response.ok) {
          console.error('Newsletter signup error:', result);
          // Show error message to user
          alert(`Ett fel uppstod: ${result.error || 'Okänt fel'}`);
          // Fortsätt ändå till nästa steg
        } else if (result.success) {
          // Show success message
          console.log(result.message);
        }
        
        // Fortsätt sedan med vanliga flödet
        setAnswers({ ...answers, [currentQuestion.id]: answer });
        setCurrentStep(currentStep + 1);
        setInputValue('');
      } catch (error) {
        console.error('Error submitting email:', error);
        alert('Ett fel uppstod vid registrering. Vänligen försök igen senare.');
        
        // Fortsätt ändå till nästa steg
        setAnswers({ ...answers, [currentQuestion.id]: answer });
        setCurrentStep(currentStep + 1);
        setInputValue('');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setAnswers({ ...answers, [currentQuestion.id]: answer });
      
      if (currentStep < translatedQuestions.length - 1) {
        setCurrentStep(currentStep + 1);
        setInputValue(''); // Clear input when advancing
      } else {
        // Start loading and prevent multiple submissions
        setLoading(true);
        setIsSubmitting(true);
        
        // Call the serverless function with all answers
        submitAnswers();
      }
    }
  };

  const submitAnswers = async () => {
    try {
      // Call the Assistant API endpoint instead, sending only the answers
      const response = await fetch('/.netlify/functions/assistant-recommendation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setRecommendation(data.recommendation);
      
      // Find venue by name now
      const venueName = data.recommendedVenueName;
      
      if (venueName) {
        // Try to find in venues by name
        const foundVenue = venues.find(v => 
          v.name.includes(venueName) || venueName.includes(v.name)
        );
        
        if (foundVenue) {
          setRecommendedVenue(foundVenue);
        } else {
          // Default to Jacy'z if no match
          setRecommendedVenue(venues.find(v => v.id.includes('jacyz')));
        }
      } else {
        // Default to Jacy'z if no venue name was extracted
        setRecommendedVenue(venues.find(v => v.id.includes('jacyz')));
      }
      
    } catch (error) {
      console.error('Error getting recommendation:', error);
      setRecommendation('Något gick fel. Vänligen försök igen senare.');
    } finally {
      setLoading(false);
      setIsSubmitting(false); // Reset submission state
    }
  };

  const userName = answers.email ? answers.email.split('@')[0] : '';
  
  // Helper function to extract amenities to display
  const getHighlightedAmenities = (venue: Venue) => {
    if (!venue || !venue.amenities) return ['Moderna faciliteter'];
    
    const result = [];
    const amenities = venue.amenities;
    
    if (amenities.includes('Wi-Fi') || amenities.includes('WiFi') || amenities.includes('Wifi')) {
      result.push('Wi-Fi');
    }
    
    if (amenities.includes('Restaurang') || amenities.includes('Restaurant')) {
      result.push('Restaurang');
    }
    
    if (amenities.includes('Bar')) {
      result.push('Bar');
    }
    
    if (amenities.includes('Parkering') || amenities.includes('Parking')) {
      result.push('Parkering');
    }
    
    if (amenities.includes('Konferensrum') || amenities.includes('Conference room')) {
      result.push('Konferensrum');
    }
    
    // Add more based on what's in your data
    return result.length > 0 ? result : ['Moderna faciliteter'];
  };
  
  // Function to get the correct image path
  const getVenueImagePath = (venue) => {
    if (!venue) return '';
    
    // First try to use venue images if available
    if (venue.images && venue.images.length > 0) {
      return venue.images[0].url || venue.images[0];
    }
    
    // Otherwise use the ID-based path
    const venueId = venue.id || '';
    return `/venues/${venueId}.jpg`;
  };

  // Extract benefits based on the answers and venue
  const getVenueBenefits = (venue, userAnswers) => {
    if (!venue) return [];
    
    const benefits = [];
    
    // Add benefit based on size match
    const peopleCount = parseInt(userAnswers.people) || 0;
    if (venue.capacity && peopleCount > 0) {
      if (peopleCount >= venue.capacity.min && peopleCount <= venue.capacity.max) {
        benefits.push('Perfekt storlek för ditt sällskap');
      }
    }
    
    // Add benefit for 5-star rating
    if (venue.rating === 5) {
      benefits.push('5-stjärnig upplevelse med högsta kvalitet');
    }
    
    // Add teambuilding benefit if mentioned
    if (userAnswers.teambuilding && userAnswers.teambuilding.length > 5) {
      benefits.push('Möjlighet till skräddarsydda team-building aktiviteter');
    }
    
    // Add location benefit for Gothenburg
    if (venue.location?.includes('Göteborg') || venue.address?.city?.includes('Göteborg')) {
      benefits.push('Centralt läge i Göteborg med bra kommunikationer');
    }
    
    // Add generic benefits if we don't have enough
    if (benefits.length < 3) {
      if (!benefits.some(b => b.includes('mat'))) {
        benefits.push('Utsökt matupplevelse från erfarna kockar');
      }
      if (!benefits.some(b => b.includes('modern'))) {
        benefits.push('Moderna faciliteter med senaste tekniken');
      }
      if (!benefits.some(b => b.includes('service'))) {
        benefits.push('Förstklassig service från professionell personal');
      }
    }
    
    return benefits.slice(0, 4); // Return max 4 benefits
  };
  
  // Format the recommendation text for better styling
  const formatRecommendation = (text: string) => {
    let formatted = text;
    
    if (text) {
      // Advanced formatting with sections
      const paragraphs = text.split('\n\n').filter(p => p.trim().length > 0);
      
      if (paragraphs.length > 1) {
        let result = '';
        
        // Introduction
        result += `<div class="mb-8 overflow-hidden">
                    <div class="text-lg font-semibold text-[#2d2f4e] mb-4 flex items-start">
                      <div class="flex-shrink-0 mr-2">
                        <Award class="w-5 h-5" />
                      </div>
                      <div class="flex-grow">
                        Vår rekommendation
                      </div>
                    </div>
                    <div class="mt-3">
                      <p class="leading-relaxed break-words">${paragraphs[0]}</p>
                    </div>
                  </div>`;
        
        // Middle section - why this is a good match
        const middleParagraphs = paragraphs.slice(1, paragraphs.length > 2 ? -1 : undefined).join('</p><p class="mt-3 leading-relaxed break-words">');
        
        result += `<div class="mb-8 overflow-hidden">
                    <div class="text-lg font-semibold text-[#2d2f4e] mb-4 flex items-start">
                      <div class="flex-shrink-0 mr-2">
                        <Clock class="w-5 h-5" />
                      </div>
                      <div class="flex-grow">
                        Varför detta passar er
                      </div>
                    </div>
                    <div class="mt-3">
                      <p class="leading-relaxed break-words">${middleParagraphs}</p>
                    </div>
                  </div>`;
        
        // Conclusion
        if (paragraphs.length > 2) {
          result += `<div class="overflow-hidden">
                      <div class="text-lg font-semibold text-[#2d2f4e] mb-4 flex items-start">
                        <div class="flex-shrink-0 mr-2">
                          <CheckCircle class="w-5 h-5" />
                        </div>
                        <div class="flex-grow">
                          Sammanfattning
                        </div>
                      </div>
                      <div class="mt-3">
                        <p class="leading-relaxed break-words">${paragraphs[paragraphs.length-1]}</p>
                      </div>
                    </div>`;
        }
        
        formatted = result;
      }
    }
    
    return formatted;
  };

  // Create a better thinking animation component
  const ThinkingAnimation = () => {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="relative w-24 h-24 mb-8">
          <div className="absolute inset-0 border-4 border-[#fabddf]/20 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-transparent border-t-[#fabddf] rounded-full animate-spin"></div>
          <div className="absolute inset-2 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2d2f4e] animate-pulse">
              <path d="M12 2v8"></path><path d="M12 18v4"></path><path d="M4.93 10.93l1.41 1.41"></path>
              <path d="M17.66 11.34l1.41-1.41"></path><path d="M19.07 17.07l-1.41-1.41"></path>
              <path d="M6.34 15.66l-1.41 1.41"></path><path d="M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"></path>
            </svg>
          </div>
        </div>
        
        <h3 className="text-lg font-medium text-gray-800 mb-2">AI-assistenten tänker...</h3>
        
        <div className="space-y-4 max-w-sm text-center">
          <p className="text-gray-600">Jag analyserar dina svar och letar efter den perfekta matchningen.</p>
          
          <div className="flex justify-center space-x-2 text-[#fabddf] font-medium animate-pulse">
            <span>●</span>
            <span className="animate-delay-100">●</span>
            <span className="animate-delay-200">●</span>
          </div>
          
          <div className="mt-3 text-sm text-gray-500">
            Detta kan ta upp till 30 sekunder
          </div>
        </div>
      </div>
    );
  };

  // Add this helper function to validate email
  const isValidEmail = (email: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm overflow-hidden">
      <div className="bg-gray-50 rounded-xl shadow-lg w-full max-w-md md:max-w-2xl overflow-hidden border border-gray-100 max-h-[90vh] flex flex-col">
        {/* Header with pink background */}
        <div className="bg-[#fabddf] p-3 sm:p-4 flex justify-between items-center flex-shrink-0">
          <h2 className="text-[#2d2f4e] text-base sm:text-lg font-medium">
            {t('aiAssistant.title')}
          </h2>
          <button 
            onClick={() => {
              onClose();
              // Optionally: remove the parameter from URL
              const url = new URL(window.location.href);
              url.searchParams.delete('ai');
              window.history.replaceState({}, '', url.toString());
            }}
            className="text-[#2d2f4e] hover:opacity-80 focus:outline-none"
            aria-label={t('aiAssistant.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
          {!recommendation ? (
            <>
              <div className="mb-8">
                {currentStep > 0 && userName && currentStep !== translatedQuestions.length - 1 && (
                  <p className="text-emerald-600 mb-2 font-medium">{t('aiAssistant.survey.greeting', {name: userName})}</p>
                )}
                
                <div className="mb-4">
                  <h3 className="text-gray-800 text-lg font-medium mb-4">{currentQuestion.text}</h3>
                  
                  {currentQuestion.options ? (
                    // Option buttons
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {currentQuestion.options.map(option => (
                        <button
                          key={option}
                          onClick={() => handleAnswer(option)}
                          className="py-3 px-4 bg-white hover:bg-gray-100 active:bg-gray-200 border border-gray-200 rounded-lg font-medium transition-colors text-gray-800"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ) : (
                    // Email input for first question
                    currentQuestion.id === 'email' ? (
                      <div className="mt-4">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (inputValue && isValidEmail(inputValue)) {
                            handleAnswer(inputValue);
                          }
                        }}>
                          <div className="mb-4">
                            <input
                              type="email"
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              placeholder="Email"
                              className={`w-full p-3 border ${!inputValue || isValidEmail(inputValue) ? 'border-gray-300' : 'border-red-400'} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#fabddf]`}
                              required
                            />
                            {inputValue && !isValidEmail(inputValue) && (
                              <p className="text-red-500 text-sm mt-1">{t('form.invalidEmail')}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center mb-4">
                            <input
                              type="checkbox"
                              id="privacy-policy"
                              checked={privacyPolicyAccepted}
                              onChange={() => setPrivacyPolicyAccepted(!privacyPolicyAccepted)}
                              className="h-4 w-4 text-[#fabddf] focus:ring-[#fabddf] rounded"
                              required
                            />
                            <label htmlFor="privacy-policy" className="ml-2 block text-sm text-gray-700">
                              {t('form.privacyConsent')} <a href="/privacy-policy" target="_blank" className="text-[#2d2f4e] underline hover:text-[#fabddf]">{t('form.privacyPolicy')}</a>
                            </label>
                          </div>
                          
                          <button
                            type="submit"
                            disabled={!inputValue || !isValidEmail(inputValue) || !privacyPolicyAccepted || isSubmitting}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-150 ${
                              !inputValue || !isValidEmail(inputValue) || !privacyPolicyAccepted
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-[#fabddf] text-[#2d2f4e] hover:bg-[#fabddf]/90 active:bg-[#fabddf]/80'
                            }`}
                          >
                            {isSubmitting ? (
                              <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-[#2d2f4e] border-t-transparent rounded-full animate-spin mr-2"></div>
                                {t('form.submitting')}
                              </div>
                            ) : (
                              t('aiAssistant.survey.next')
                            )}
                          </button>
                        </form>
                      </div>
                    ) : (
                      // Free text input for other questions
                      <div className="mt-4">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (inputValue) {
                            handleAnswer(inputValue);
                          }
                        }}>
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={t('aiAssistant.placeholder')}
                            className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#fabddf]"
                          />
                          
                          <div className="flex justify-end gap-2 mt-4">
                            {currentStep > 0 && (
                              <button
                                type="button"
                                onClick={() => setCurrentStep(currentStep - 1)}
                                className="py-2 px-4 bg-white hover:bg-gray-100 active:bg-gray-200 border border-gray-300 rounded-lg text-gray-700 font-medium"
                              >
                                {t('aiAssistant.survey.back')}
                              </button>
                            )}
                            
                            <button
                              type="submit"
                              disabled={!inputValue}
                              className="py-2 px-4 bg-[#fabddf] text-[#2d2f4e] hover:bg-[#fabddf]/90 active:bg-[#fabddf]/80 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('aiAssistant.survey.next')}
                            </button>
                          </div>
                        </form>
                      </div>
                    )
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  {t('aiAssistant.recommendationTitle')}
                </h3>
                <div className="prose prose-sm text-gray-600" dangerouslySetInnerHTML={{ __html: recommendation }}></div>
              </div>
              
              {recommendedVenue && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-4">
                    <h3 className="font-medium text-lg text-gray-800">{recommendedVenue.name}</h3>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{recommendedVenue.address}</span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="h-4 w-4 mr-1" />
                        <span>{recommendedVenue.capacity?.min}-{recommendedVenue.capacity?.max} {t('people')}</span>
                      </div>
                      
                      <button
                        onClick={() => {
                          // Navigate to venue details
                          window.location.href = `/venue/${recommendedVenue.id}`;
                        }}
                        className="text-sm text-[#2d2f4e] font-medium hover:text-[#fabddf]"
                      >
                        {t('venues.viewDetails')} →
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Contact Jacy'z */}
              {jacyzVenue && (
                <div className="bg-[#2d2f4e] text-white rounded-lg overflow-hidden mt-6">
                  <div className="p-4">
                    <div className="flex items-center">
                      <Award className="h-5 w-5 text-[#fabddf] mr-2" />
                      <h3 className="font-medium">{t('sponsors.featured')}</h3>
                    </div>
                    
                    <h4 className="font-medium text-lg mt-2">{jacyzVenue.name}</h4>
                    
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center text-sm">
                        <MapPin className="h-4 w-4 mr-2 text-[#fabddf]" />
                        <span>{jacyzVenue.address}</span>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <Phone className="h-4 w-4 mr-2 text-[#fabddf]" />
                        <a 
                          href={`tel:${JACYZ_PHONE.replace(/\s/g, '')}`}
                          className="hover:underline"
                        >
                          {JACYZ_PHONE}
                        </a>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <Clock className="h-4 w-4 mr-2 text-[#fabddf]" />
                        <span>{t('aiAssistant.quickResponse')}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <a 
                        href={`/venue/${jacyzVenue.id}`}
                        className="block w-full py-2 text-center bg-[#fabddf] text-[#2d2f4e] rounded-lg font-medium hover:bg-[#fabddf]/90"
                      >
                        {t('venues.viewDetails')}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 